import { useState, useCallback, useEffect } from 'react';
import { useMemorizeFilters } from 'hooks/useMemorizeInputsFilters';

/**
 * Um hook personalizado para gerenciar campos de importação em um formulário.
 *
 * @param {Object} options - Opções de configuração
 * @param {Array} options.defaultRequiredFields - Campos obrigatórios padrão que devem ser incluídos
 * @param {string} options.entityName - Nome da entidade sendo importada
 * @param {string|number} options.recordTypeId - ID do tipo de registro
 * @param {Function} options.getCustomFields - Função para buscar campos personalizados do servidor
 * @param {string} options.filterEntity - Nome da entidade usada para memorização de filtros
 *
 * @returns {Object} Um objeto contendo:
 * @returns {Array} fields - Todos os campos atualmente selecionados (obrigatórios e opcionais)
 * @returns {Array} optionalFields - Campos opcionais disponíveis que podem ser adicionados
 * @returns {string} option - Campo opcional atualmente selecionado
 * @returns {Function} setOption - Função para definir o campo opcional selecionado
 * @returns {boolean} isLoading - Indicador de estado de carregamento
 * @returns {Object} validationErrors - Objeto contendo erros de validação dos campos
 * @returns {Function} setValidationErrors - Função para atualizar erros de validação
 * @returns {boolean} useBrazilianDDI - Se deve usar DDI brasileiro para campos de telefone
 * @returns {Function} handleUseBrazilianDDI - Manipulador para alternar a opção de DDI brasileiro
 * @returns {Function} onAddField - Adiciona o campo opcional selecionado ao formulário
 * @returns {Function} onRemoveField - Remove um campo do formulário
 * @returns {Function} validateForm - Valida os dados do formulário e retorna erros
 * @returns {Function} saveImportFields - Memoriza a configuração atual dos campos
 * @returns {Function} resetFieldsState - Reinicia o estado dos campos para o padrão
 */

export const useImportFields = ({
  defaultRequiredFields,
  recordTypeId,
  getCustomFields,
  filterEntity
}) => {
  const [fields, setFields] = useState(defaultRequiredFields);
  const [protocolFields, setProtocolFields] = useState([]);
  const [templateFields, setTemplateFields] = useState([]);
  const [optionalFields, setOptionalFields] = useState([]);
  const [option, setOption] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fieldsInitialized, setFieldsInitialized] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [useBrazilianDDI, setUseBrazilianDDI] = useState(false);

  const { memorizeFilters, getMemorizedFilters } =
    useMemorizeFilters(filterEntity);

  useEffect(() => {
    setProtocolFields(fields.filter((field) => field?.isProtocolParams));
    setTemplateFields(fields.filter((field) => field?.isTemplateParams));
  }, [fields]);

  const handleUseBrazilianDDI = useCallback(
    (e) => {
      const checked = e.target.checked;
      setUseBrazilianDDI(checked);

      fields.forEach((field) => {
        if (field.type === 'TELEPHONE' && validationErrors[field.name]) {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[field.name];
            return newErrors;
          });
        }
      });
    },
    [fields, validationErrors]
  );

  const onAddField = useCallback(() => {
    if (!option) return;

    const selectedField = optionalFields.find((f) => f.name === option);
    if (!selectedField) return;

    setFields((prev) => [...prev, selectedField]);
    setOptionalFields((prev) => prev.filter((f) => f.name !== option));
    setOption('');
  }, [option, optionalFields]);

  const onRemoveField = useCallback(
    (fieldName) => {
      const fieldToRemove = fields.find((f) => f.name === fieldName);
      if (fieldToRemove?.isRequired) return;

      if (validationErrors[fieldName]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }

      setFields((prev) => prev.filter((f) => f.name !== fieldName));
      if (fieldToRemove) {
        setOptionalFields((prev) => [...prev, fieldToRemove]);
      }
    },
    [fields, validationErrors]
  );

  const validateForm = useCallback(
    (data) => {
      const errors = {};

      fields.forEach((field) => {
        const value = data[field.name];

        if (field.type === 'TELEPHONE') {
          if (useBrazilianDDI) {
            if (!value?.number) {
              errors[field.name] = `Selecione a coluna do número de telefone`;
            }
          } else {
            if (!value?.ddi || !value?.number) {
              errors[field.name] = `Preencha o DDI e o número do telefone`;
            }
          }
        } else if (!value && field.isRequired) {
          errors[field.name] = `Campo obrigatório`;
        }
      });

      return errors;
    },
    [fields, useBrazilianDDI]
  );

  const validateFields = useCallback(
    ({ data, fileData, useBrazilianDDI, fields }) => {
      const errorsFields = [];
      let sanitizeData = [];
      const headersNames = fileData[0];
      sanitizeData.push(headersNames);

      const ddiColumnNames = [];
      let numbers = new Set();
      Object.keys(data).forEach((key) => {
        if (typeof data[key] === 'object' && data[key]?.ddi) {
          ddiColumnNames.push(data[key]?.ddi);
        }
      });

      const fieldsConfigMap = new Map(
        Object.entries(data ?? {}).map(([dbParse, spreadsheetColumnName]) => [
          typeof spreadsheetColumnName === 'object'
            ? spreadsheetColumnName.number
            : spreadsheetColumnName,
          fields?.find((f) => dbParse === f?.name)
        ])
      );

      // Percorre por cada linha da planilha
      for (let indexRow = 0; indexRow <= fileData.length - 1; indexRow += 1) {
        if (indexRow === 0) continue;
        let error = false;
        const row = fileData[indexRow];

        //Percorre cada coluna da linha atual
        for (
          let indexCol = 0;
          indexCol <= headersNames.length - 1;
          indexCol += 1
        ) {
          const valueColumn = row[indexCol];
          const colName = headersNames[indexCol];

          // Validação do campo DDI
          if (!useBrazilianDDI && ddiColumnNames.includes(colName)) {
            if (!valueColumn) {
              errorsFields.push({
                row: indexRow,
                herderName: colName,
                message: 'Campo vazio (DDI não pode ser nulo) '
              });
              error = true;
              continue;
            }
            // Verificar se é número
            if (isNaN(valueColumn)) {
              errorsFields.push({
                row: indexRow,
                herderName: colName,
                message: 'DDI deve ser um número'
              });
              error = true;
              continue;
            }
          }

          const configField = fieldsConfigMap.get(colName);
          if (!configField) continue;
          // Validação para campos obrigatórios
          if (configField?.isRequired) {
            if (!valueColumn || valueColumn?.length === 0) {
              errorsFields.push({
                row: indexRow,
                herderName: colName,
                message: 'Campo vazio'
              });
              error = true;
              continue;
            }
          }

          if (configField?.name === 'responsibleLogin') {
            if (!valueColumn || valueColumn?.length === 0) {
              errorsFields.push({
                row: indexRow,
                herderName: colName,
                message: 'Campo vazio'
              });
              error = true;
              continue;
            }
          }

          if (valueColumn.length > 100) {
            errorsFields.push({
              row: indexRow,
              herderName: colName,
              message: 'Campo com mais de 100 caracteres'
            });
            error = true;
            continue;
          }

          // Validação para campos de telefone
          if (configField.type === 'TELEPHONE') {
            if (numbers.has(valueColumn)) {
              errorsFields.push({
                row: indexRow,
                herderName: colName,
                message: 'Telefone duplicado'
              });

              numbers.add(valueColumn);
              error = true;
              continue;
            }

            if (useBrazilianDDI) {
              if (
                valueColumn.trim().length !== 10 &&
                valueColumn.trim().length !== 11
              ) {
                errorsFields.push({
                  row: indexRow,
                  herderName: colName,
                  message: 'Telefone com tamanho inválido'
                });
                error = true;
              }
            }
            numbers.add(valueColumn);
          }
        }

        // Adiciona somente os dados que não possui erros
        if (!error) sanitizeData.push(row);
      }
      return {
        errorsFields,
        sanitizeData
      };
    },
    [fields, useBrazilianDDI]
  );

  const saveImportFields = useCallback(
    (fieldsToSave) => {
      if (!filterEntity) return;
      const recordTypeIdNumber = Number(recordTypeId);
      const memorizedFields = getMemorizedFilters() || [];

      const existingFieldIndex = memorizedFields?.findIndex?.(
        (field) => field.recordTypeId === recordTypeIdNumber || []
      );

      if (existingFieldIndex > -1) {
        const updatedFields = [...memorizedFields];
        updatedFields[existingFieldIndex] = {
          ...updatedFields[existingFieldIndex],
          fields: fieldsToSave
        };
        memorizeFilters(updatedFields);
      } else {
        const newFieldConfig = {
          recordTypeId: recordTypeIdNumber,
          fields: [...fieldsToSave]
        };
        memorizeFilters([...memorizedFields, newFieldConfig]);
      }
    },
    [recordTypeId, getMemorizedFilters, memorizeFilters, filterEntity]
  );

  useEffect(() => {
    if (!fieldsInitialized) {
      const loadFields = async () => {
        if (getCustomFields) {
          try {
            setIsLoading(true);
            const customFields = await getCustomFields();

            const requiredDefaultFields = defaultRequiredFields.filter(
              (field) => field.isRequired
            );
            const optionalDefaultFields = defaultRequiredFields.filter(
              (field) => !field.isRequired
            );

            const requiredCustomFields = [];
            const optionalCustomFields = [];

            customFields.forEach((field) => {
              const fieldDTO = {
                id: field.id,
                name: field.name,
                label: field.label,
                type: field.type,
                isRequired: field.recordTypesConfig.isRequired
              };

              if (field.recordTypesConfig.isRequired) {
                requiredCustomFields.push(fieldDTO);
              } else {
                optionalCustomFields.push(fieldDTO);
              }
            });

            const allRequiredFields = [
              ...requiredDefaultFields,
              ...requiredCustomFields
            ];
            const allOptionalFields = [
              ...optionalDefaultFields,
              ...optionalCustomFields
            ];

            try {
              const memorizedFields = getMemorizedFilters();
              const currentRecordTypeFields = memorizedFields?.find(
                (field) => field.recordTypeId === Number(recordTypeId)
              )?.fields;

              if (currentRecordTypeFields) {
                const memorizedOptional = currentRecordTypeFields.filter(
                  (savedField) =>
                    !savedField.isRequired &&
                    allOptionalFields.some((opt) => opt.id === savedField.id)
                );

                const fieldsToShow = [
                  ...allRequiredFields,
                  ...memorizedOptional
                ];
                setFields(fieldsToShow);

                const usedFieldIds = fieldsToShow.map((f) => f.id);
                setOptionalFields(
                  allOptionalFields.filter(
                    (field) => !usedFieldIds.includes(field.id)
                  )
                );
              } else {
                setFields(allRequiredFields);
                setOptionalFields(allOptionalFields);
              }
            } catch (error) {
              setFields(allRequiredFields);
              setOptionalFields(allOptionalFields);
            }
          } catch (error) {
            setFields(defaultRequiredFields.filter((f) => f.isRequired));
            setOptionalFields(
              defaultRequiredFields.filter((f) => !f.isRequired)
            );
          } finally {
            setFieldsInitialized(true);
            setIsLoading(false);
          }
        } else {
          setFields(defaultRequiredFields.filter((f) => f.isRequired));
          setOptionalFields(defaultRequiredFields.filter((f) => !f.isRequired));
          setFieldsInitialized(true);
          setIsLoading(false);
        }
      };

      loadFields();
    }
  }, [
    fieldsInitialized,
    getCustomFields,
    defaultRequiredFields,
    recordTypeId,
    getMemorizedFilters
  ]);

  const resetFieldsState = useCallback(() => {
    setFieldsInitialized(false);
    setValidationErrors({});
    setFields([]);
    setUseBrazilianDDI(false);
    setOption('');
  }, []);

  return {
    fields,
    setFields,
    protocolFields,
    setProtocolFields,
    templateFields,
    setTemplateFields,
    optionalFields,
    option,
    setOption,
    isLoading,
    validationErrors,
    setValidationErrors,
    useBrazilianDDI,
    handleUseBrazilianDDI,
    onAddField,
    onRemoveField,
    validateForm,
    saveImportFields,
    resetFieldsState,
    validateFields
  };
};
